import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { ToastComponent } from '../shared/toast/toast.component';
import { AuthService } from '../services/auth.service';
import { RecordService } from '../services/record.service';
import { ClientService } from '../services/client.service';
import { Client } from '../shared/models/client.model';
import { ee, dateRegexp } from '../shared/constants';
import { generateStatsCSV, removeTooltipElement, saveFile, setClassInvalid } from '../shared/functions';
import { exportChart, generateBarChart, generateDonutChart } from '../shared/chartFunctions';
import { BarChartParams, EsSearchStats, MonthsAvg } from '../shared/types';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
  standalone: false
})
export class StatisticsComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private recordService = inject(RecordService);
  private clientService = inject(ClientService);
  toast = inject(ToastComponent);


  isLoading = {
    stats: true
  };
  isSearching = false;
  skip = {
    user: 0,
    client: 0,
    clientStudios: 0,
    clientMeeting: 0,
    stakeholder: 0,
    commission: 0,
    media: 0,
  };
  activity = {
    clientStudios: 'UTOPIA Studios - URANIA',
    clientMeeting: 'Meeting / Relationship with Institution',
  };
  dateRegexp = dateRegexp;
  setClassInvalid = setClassInvalid;
  exportChart = exportChart;
  // Vars
  clients: Client[] = [];
  // Filters
  // user = '';
  client = '';
  startDate = '';
  endDate = '';
  // Charts
  chartParams: BarChartParams = {
    label: 'activities',
  };
  counters: MonthsAvg = { months: [{ month: '', count: 0 }], avg: 0 };
  countersStakeholders: MonthsAvg = { months: [{ month: '', count: 0 }], avg: 0 };
  barChartWeek;
  barChartActivity;
  barChartUser;
  donutChartDepartment;
  barChartClient;
  barChartClientStudios;
  barChartClientMeeting;
  barChartCluster;
  barChartWeekStakeholders;
  barChartParty;
  barChartMedia;
  barChartCommission;
  barChartStakeholder;
  csvStats: EsSearchStats = {};
  canViewStats = false;
  currentContent = 'statistics';
  menuItems = [
    { name: 'Statistics', link: 'statistics', icon: 'bar-chart' },
  ];

  ngOnInit(): void {
    ee.on('changeContent', (data: string) => this.currentContent = data);
    this.canViewStats = this.auth.currentUser.permissions.includes('viewStats');
    this.getClients();
    this.getStats();
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    removeTooltipElement();
    this.destroyCharts();
  }

  destroyCharts(): void {
    this.barChartWeek?.destroy();
    this.barChartActivity?.destroy();
    this.barChartUser?.destroy();
    this.donutChartDepartment?.destroy();
    this.barChartClient?.destroy();
    this.barChartClientStudios?.destroy();
    this.barChartClientMeeting?.destroy();
    this.barChartCluster?.destroy();
    this.barChartWeekStakeholders?.destroy();
    this.barChartParty?.destroy();
    this.barChartMedia?.destroy();
    this.barChartCommission?.destroy();
    this.barChartStakeholder?.destroy();
  }

  getClients(): void {
    this.clientService.search({ active: '1' })
      .subscribe({
        next: res => this.clients = res.results,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getStats(): void {
    this.isLoading.stats = true;
    this.destroyCharts();
    const params = {
      model: undefined,
      id: undefined,
      startDate: this.startDate,
      endDate: this.endDate,
      activity: undefined,
    };
    if (this.client) {
      params.model = 'client';
      params.id = this.client;
    }
    forkJoin([
      this.recordService.getStatsCounters(params),
      this.recordService.getStatsByWeek(params),
      this.recordService.getStatsByActivity(params),

      this.recordService.getStatsByUser(params),
      this.recordService.getStatsByDepartment(params),

      this.recordService.getStatsByClient(params),
      this.recordService.getStatsByClient({ ...params, activity: 'UTOPIA Studios - URANIA' }),
      this.recordService.getStatsByClient({ ...params, activity: 'Meeting / Relationship with Institution' }),
      this.recordService.getStatsByCluster(params),

      this.recordService.getStatsCounters({ ...params, isPoliticianFiltered: true }),
      this.recordService.getStatsByWeek({ ...params, isPoliticianFiltered: true }),
      this.recordService.getStatsByParty({ ...params, isPoliticianFiltered: true }),
      this.recordService.getStatsByMedia({ ...params, isPoliticianFiltered: true }),
      this.recordService.getStatsByCommission({ ...params, isPoliticianFiltered: true }),

      this.recordService.getStatsByStakeholder(params),
    ])
      .subscribe({
        next: ([statsCounters, statsByWeek, statsByActivity,
          statsByUser, statsByDepartment, statsByClient,
          statsByClientStudios, statsByClientMeeting, statsByCluster,
          statsCountersStakeholders, statsByWeekStakeholders,
          statsByParty, statsByMedia, statsByCommission, statsByStakeholder]) => {
          this.counters = statsCounters as MonthsAvg;
          this.barChartWeek = generateBarChart(statsByWeek, '1', { ...this.chartParams, verticalBars: true });
          this.barChartActivity = generateBarChart(statsByActivity, '2', this.chartParams);
          this.barChartUser = generateBarChart(statsByUser, '3', this.chartParams);
          this.donutChartDepartment = generateDonutChart(statsByDepartment, '1', '#fff');
          this.barChartClient = generateBarChart(statsByClient, '4', this.chartParams);
          this.barChartCluster = generateBarChart(statsByCluster, '5', this.chartParams);
          this.barChartClientStudios = generateBarChart(statsByClientStudios, '6', this.chartParams);
          this.barChartClientMeeting = generateBarChart(statsByClientMeeting, '7', this.chartParams);
          this.countersStakeholders = statsCountersStakeholders as MonthsAvg;
          this.barChartWeekStakeholders = generateBarChart(statsByWeekStakeholders, '8', { ...this.chartParams, verticalBars: true });
          this.barChartParty = generateBarChart(statsByParty, '9', { ...this.chartParams, isPartiesChart: true });
          this.barChartMedia = generateBarChart(statsByMedia, '12', this.chartParams);
          this.barChartCommission = generateBarChart(statsByCommission, '10', this.chartParams);
          this.barChartStakeholder = generateBarChart(statsByStakeholder, '11', this.chartParams);
          this.csvStats = {
            statsByWeek, statsByActivity, statsByUser, statsByDepartment, statsByClient,
            statsByClientStudios, statsByClientMeeting, statsByCluster,
            statsByWeekStakeholders, statsByParty, statsByMedia, statsByCommission, statsByStakeholder
          };
          this.isLoading.stats = false;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  // model: 'user' | 'client' | 'stakeholder' | 'commission'
  getStatsByModelSkip(nextOrPrev: 'next' | 'prev', model: string): void {
    const criteria = {
      user: {
        chart: 'barChartUser',
        num: '3',
        statsService: 'getStatsByUser'
      },
      client: {
        chart: 'barChartClient',
        num: '4',
        statsService: 'getStatsByClient'
      },
      clientStudios: {
        chart: 'barChartClientStudios',
        num: '6',
        statsService: 'getStatsByClient'
      },
      clientMeeting: {
        chart: 'barChartClientMeeting',
        num: '7',
        statsService: 'getStatsByClient'
      },
      commission: {
        chart: 'barChartCommission',
        num: '10',
        statsService: 'getStatsByCommission'
      },
      stakeholder: {
        chart: 'barChartStakeholder',
        num: '11',
        statsService: 'getStatsByStakeholder'
      },
      media: {
        chart: 'barChartMedia',
        num: '12',
        statsService: 'getStatsByMedia'
      },
    };
    if (nextOrPrev === 'next') {
      this.skip[model] += 10;
    } else {
      this.skip[model] = this.skip[model] <= 0 ? 0 : this.skip[model] - 10;
    }
    this[criteria[model].chart]?.destroy();
    const params = {
      model: undefined,
      id: undefined,
      startDate: this.startDate,
      endDate: this.endDate,
      isPoliticianFiltered: model === 'commission' || model === 'media' || undefined,
      skip: this.skip[model],
      activity: this.activity[model],
    };
    this.recordService[criteria[model].statsService](params)
      .subscribe({
        next: res => this[criteria[model].chart] = generateBarChart(res, criteria[model].num, this.chartParams),
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  downloadCSV(what: string, header: string) {
    saveFile(generateStatsCSV(this.csvStats[what], header), `${what}.csv`);
  }

}
